"use client";

import { openModal } from "@decibeltrade/web-core/modal";
import dynamic from "next/dynamic";
import { useTranslations } from "next-intl";
import { useCallback } from "react";

import { isFunElement } from "@/components/fun-deposit/fun-overlay";
import { useAnalytics } from "@/contexts/analytics-context";
import { useAuthState } from "@/hooks/use-auth-state";
import { useRequireDelegation } from "@/hooks/use-require-delegation";
import { useRequireTradingAllowed } from "@/hooks/use-require-trading-allowed";
import { Analytics } from "@/lib/analytics";

import { toast } from "../../toast";
import { LazyModalCloseProvider } from "../lazy-modal-close";
import { DepositModalSkeleton } from "./deposit-modal-skeleton";
import { type DepositAssetValue } from "./deposit-modal-state";

const MODAL_ID = "deposit";

export type { DepositAssetValue } from "./deposit-modal-state";

/**
 * The modal's own module is the entry point to the cross-chain bridge stack (Wormhole, Solana
 * web3, the Mesh SDK), which is far and away the largest thing the app can load. Kept behind a
 * dynamic import it stays out of every route's first load — the trade page included, where the
 * only reference to it is a Deposit button most sessions never press.
 */
const loadDepositModal = () => import("./deposit-modal");

const DepositModalContent = dynamic(() => loadDepositModal().then((m) => m.DepositModal), {
  ssr: false,
  loading: () => <DepositModalSkeleton />,
});

/**
 * Starts the chunk downloading before the click that needs it — wire to hover/focus of anything
 * that opens the deposit modal. Repeat calls are free: the import resolves from the module cache.
 */
export const preloadDepositModal = () => void loadDepositModal();

interface DepositModalOpenOptions {
  /**
   * Which asset to open on. Callers name it in the modal's own vocabulary, so a row that knows it
   * holds APT can say so without a second set of token names to keep in step.
   */
  defaultAsset?: DepositAssetValue;
}

export const openDepositModal = (analytics: Analytics, options?: DepositModalOpenOptions) => {
  openModal({
    id: MODAL_ID,
    ownsElement: isFunElement,
    renderContent: (modalProps) => (
      <LazyModalCloseProvider value={modalProps.close}>
        <DepositModalContent {...modalProps} defaultAsset={options?.defaultAsset} />
      </LazyModalCloseProvider>
    ),
  });
  analytics.ui.modalOpened({ modalName: MODAL_ID });
};

export const useOpenDepositModal = () => {
  const requireTradingAllowed = useRequireTradingAllowed();
  const requireDelegation = useRequireDelegation();
  const Analytics = useAnalytics();
  const tToast = useTranslations("toast");
  const { state } = useAuthState();

  return useCallback(
    (optionsOrEvent?: DepositModalOpenOptions | React.MouseEvent) => {
      const options =
        optionsOrEvent && "defaultAsset" in optionsOrEvent ? optionsOrEvent : undefined;
      if (state === "SESSION_KEY_IMPORTED") {
        toast({
          variant: "error",
          title: tToast("titles.depositsNotAvailable"),
          description: tToast("summaries.depositsNotAvailableSessionKeyWarning"),
        });
        return;
      }
      requireTradingAllowed(requireDelegation(() => openDepositModal(Analytics, options)))();
    },
    [requireTradingAllowed, requireDelegation, Analytics, tToast, state],
  );
};
